import React, { useState } from 'react';
import { useLayerControl } from './hooks/useLayerControl';
import './styles/LayerControl.css';

const LayerControl = ({ map, mapReady, currentMode }) => {
  // Use VRP layer control hook
  const { 
    layerVisibility, 
    toggleLayer,
    showAllLayers,
    hideAllLayers,
    isLayerDisabledForMode,
    autoHideInfo
  } = useLayerControl(map, mapReady, currentMode);

  // Whether layer control panel is expanded
  const [isExpanded, setIsExpanded] = useState(false);

  // Layer configuration - simplified to match TSP style
  const layers = [
    {
      key: 'autoPath',
      label: 'Auto Path',
      description: 'Algorithm-generated optimal path',
      color: '#2196F3',
      isModeDisabled: isLayerDisabledForMode('autoPath')
    },
    {
      key: 'customPath',
      label: 'Custom Path',
      description: 'User manually created path',
      color: '#EA4335',
      isModeDisabled: isLayerDisabledForMode('customPath')
    },
    {
      key: 'locationPoints',
      label: 'Location Points',
      description: 'Location markers and numbers',
      color: '#3498db',
      isModeDisabled: isLayerDisabledForMode('locationPoints')
    },
    {
      key: 'durationLabels',
      label: 'Duration Labels',
      description: autoHideInfo.shouldAutoHide 
        ? `Connection duration info (force hidden at city level)`
        : 'Duration labels for connection lines',
      color: '#FF9800',
      isAutoHidden: autoHideInfo.isAutoHidden,
      isDisabled: autoHideInfo.shouldAutoHide, // Disabled when should auto-hide (city level)
      isModeDisabled: isLayerDisabledForMode('durationLabels')
    },
    {
      key: 'connections',
      label: 'Connection Lines',
      description: 'Transport mode connection lines between points',
      color: '#9C27B0',
      isModeDisabled: isLayerDisabledForMode('connections')
    }
  ];

  // Get mode-specific help text
  const getModeHelpText = () => {
    switch (currentMode) {
      case 'Setup Path Points':
        return 'In Setup mode, location points and connection lines can be controlled. Add locations to see path options.';
      case 'Auto Find Path':
        return 'Auto mode shows algorithm-generated routes. Connection lines remain available for viewing.';
      case 'Custom Path':
        return 'Custom mode allows manual route editing. Connection lines remain available for viewing.';
      case 'Connection Mode':
        return 'Connection mode allows creating transport connections between points. All layers are available for control.';
      case 'Comparison Mode':
        return 'Comparison mode shows all available routes and connections. Toggle layers to compare different solutions.';
      default:
        return 'Control layer visibility to customize your view.';
    }
  };

  return (
    <div className={`layer-control ${isExpanded ? 'expanded' : 'collapsed'}`}>
      {/* Control panel header */}
      <div 
        className="layer-control-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="header-content">
          <span className="header-title">Layer Control</span>
        </div>
        <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
          ▼
        </span>
      </div>

      {/* Layer control list */}
      {isExpanded && (
        <div className="layer-control-body">
          {layers.map(layer => {
            // Combine all disable conditions
            const totalDisabled = layer.isDisabled || layer.isModeDisabled;
            
            return (
              <div key={layer.key} className={`layer-item ${layer.isAutoHidden ? 'auto-hidden' : ''} ${totalDisabled ? 'disabled' : ''} ${layer.isModeDisabled ? 'mode-disabled' : ''}`}>
                <div className="layer-info">
                  <div className="layer-header">
                    <span className="layer-label">
                      {layer.label}
                      {layer.isAutoHidden && (
                        <span className="auto-hide-indicator" title="Currently force hidden due to zoom level">
                          🚫
                        </span>
                      )}
                      {layer.isModeDisabled && (
                        <span className="mode-disabled-indicator" title={`Not operable in current mode (${currentMode})`}>
                          🔒
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="layer-description">
                    {layer.description}
                    {layer.isModeDisabled && (
                      <span className="mode-disabled-hint"> (unavailable in current mode)</span>
                    )}
                  </div>
                </div>
                
                <label className={`layer-toggle ${totalDisabled ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={layer.key === 'durationLabels' && layer.isAutoHidden ? false : layerVisibility[layer.key]}
                    onChange={() => {
                      // Check if disabled due to mode
                      if (layer.isModeDisabled) {
                        return false;
                      }
                      // Check if disabled due to auto-hide
                      if (layer.isDisabled) {
                        return false;
                      }
                      return toggleLayer(layer.key);
                    }}
                    disabled={totalDisabled}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LayerControl;
